import * as React from 'react';
import { ChevronDownIcon, ExternalLinkIcon } from '@radix-ui/react-icons';
import { Link, useLocation } from 'react-router-dom';

import config, { Routes } from 'config/constants';

import { selectUserInfo } from 'app/store/user';
import { useAppSelector } from 'app/store';
import { isLegacyBilling } from 'lib/utils';
import { cn } from 'lib/utils';
import { canAccessRoute } from 'app/access';
import { useFlags } from 'providers/FlagsProvider';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from 'components/ui/navigation-menu';

interface NavLeaf {
  title: string;
  path: string;
  target?: string;
  /** `end` pushes the item to the right of the row via `margin-left: auto`. */
  placement?: 'start' | 'end';
}

interface NavGroup {
  title: string;
  children: NavLeaf[];
  /** `end` pushes the item to the right of the row via `margin-left: auto`. */
  placement?: 'start' | 'end';
}

type NavItem = NavLeaf | NavGroup;

const isGroup = (item: NavItem): item is NavGroup => 'children' in item;

const isPathActive = (pathname: string, target: string) =>
  // Treat the first path segment as the active match so /orders/123 still
  // highlights the Orders tab. `startsWith` alone is too greedy when one
  // route is a prefix of another (we don't have that today, but cheap
  // insurance).
  pathname === target || pathname.startsWith(target.replace(/\/$/, '') + '/');

const isItemActive = (pathname: string, item: NavItem): boolean =>
  isGroup(item)
    ? item.children.some((c) => isPathActive(pathname, c.path))
    : isPathActive(pathname, item.path);

const itemKey = (item: NavItem) => (isGroup(item) ? `group:${item.title}` : item.path);

const DOCUMENTATION_ITEM: NavLeaf = {
  title: 'Documentation',
  path: 'https://docs.rye.com',
  target: '_blank',
};

function useNavTree(): NavItem[] {
  const location = useLocation();
  const userInfo = useAppSelector(selectUserInfo);
  const { billingPeriodSettings } = userInfo || {};
  const legacy = isLegacyBilling(billingPeriodSettings);
  const { teamTabEnabled } = useFlags();

  if (location.pathname === Routes.Payment) {
    return [];
  }

  if (location.pathname === Routes.NoAccess) {
    return [
      {
        title: 'Developers',
        placement: 'end',
        children: [DOCUMENTATION_ITEM],
      },
    ];
  }

  const leaf = (path: Routes): NavLeaf | null => {
    if (!canAccessRoute(userInfo, path)) {
      return null;
    }

    const r = config.routesMap.find((m) => m.path === path);
    return r ? { title: r.title, path: r.path } : null;
  };

  const developers: NavGroup = {
    title: 'Developers',
    placement: 'end',
    children: legacy
      ? [
          { title: 'API Keys', path: Routes.ApiKeys },
          { title: 'GraphQL', path: Routes.Graphql },
          { title: 'Resources', path: Routes.Resources },
        ].filter((item) => canAccessRoute(userInfo, item.path))
      : [
          ...(canAccessRoute(userInfo, Routes.ApiKeys)
            ? [{ title: 'API Keys', path: Routes.ApiKeys }]
            : []),
          DOCUMENTATION_ITEM,
        ],
  };

  return [
    leaf(Routes.Dashboard),
    leaf(Routes.Account),
    teamTabEnabled ? leaf(Routes.Team) : null,
    legacy ? leaf(Routes.Requests) : null,
    leaf(Routes.CheckoutIntents),
    // Commissions ship with the v2/subscription system only. Only hide once
    // billing has loaded; while it's pending, leave the entry so subscription
    // users don't see Commissions disappear from the nav after a brief flicker.
    billingPeriodSettings && legacy ? null : leaf(Routes.Commissions),
    leaf(Routes.Billing),
    legacy ? leaf(Routes.Orders) : null,
    legacy ? leaf(Routes.Merchants) : null,
    leaf(Routes.Catalog),
    developers,
  ].filter((item): item is NavItem => item !== null);
}

// Animated lime underline that tracks whichever item is active for the
// current route. Measures the active item's bounds with a layout effect
// and animates left/width via CSS transitions (re-measures on resize).
function useActiveUnderline(activeKey: string | null) {
  const listRef = React.useRef<HTMLUListElement>(null);
  const itemRefs = React.useRef<Map<string, HTMLElement>>(new Map());
  const [box, setBox] = React.useState<{ left: number; width: number } | null>(null);

  const setItemRef = React.useCallback((key: string, el: HTMLElement | null) => {
    if (el) {
      itemRefs.current.set(key, el);
    } else {
      itemRefs.current.delete(key);
    }
  }, []);

  const measure = React.useCallback(() => {
    if (!activeKey || !listRef.current) {
      setBox(null);
      return;
    }
    const target = itemRefs.current.get(activeKey);
    if (!target) {
      setBox(null);
      return;
    }
    const list = listRef.current.getBoundingClientRect();
    const rect = target.getBoundingClientRect();
    setBox({ left: rect.left - list.left, width: rect.width });
  }, [activeKey]);

  React.useLayoutEffect(() => {
    measure();
  }, [measure]);

  React.useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  return { listRef, setItemRef, box };
}

const Nav = () => {
  const location = useLocation();
  const items = useNavTree();
  const activeKey = items.find((i) => isItemActive(location.pathname, i)) ?? null;
  const { listRef, setItemRef, box } = useActiveUnderline(activeKey ? itemKey(activeKey) : null);

  if (items.length === 0) {
    return null;
  }

  return (
    <NavigationMenu className="w-full max-w-none justify-start [&>div]:w-full">
      <NavigationMenuList ref={listRef} className="relative w-full">
        {items.map((item) => {
          const key = itemKey(item);
          const active = isItemActive(location.pathname, item);

          if (isGroup(item)) {
            return (
              <NavigationMenuItem
                key={key}
                ref={(el: HTMLLIElement | null) => setItemRef(key, el)}
                className={item.placement === 'end' ? 'ml-auto' : undefined}
              >
                <NavigationMenuTrigger
                  data-active={active ? '' : undefined}
                  className="cursor-pointer"
                  // Radix toggles the trigger closed on click; suppress
                  // that so hover-open isn't undone by a stray click.
                  onClick={(e) => {
                    if (e.currentTarget.dataset.state === 'open') {
                      e.preventDefault();
                    }
                  }}
                >
                  {item.title}
                  <ChevronDownIcon
                    className="-mr-1 h-3 w-3 opacity-70 transition-transform duration-200 group-data-[state=open]/trigger:rotate-180"
                    aria-hidden="true"
                  />
                </NavigationMenuTrigger>
                <NavigationMenuContent
                  className={item.placement === 'end' ? 'left-auto right-0' : undefined}
                >
                  <ul className="grid w-[220px] gap-1 p-2">
                    {item.children.map((child) => (
                      <li key={child.path}>
                        <NavigationMenuLink asChild>
                          <Link
                            to={child.path}
                            target={child.target}
                            className={cn(
                              'flex items-center gap-1.5 px-3 py-2 font-sans text-sm no-underline transition-colors hover:bg-[var(--surface-2)]',
                              isPathActive(location.pathname, child.path)
                                ? 'text-[var(--text)]'
                                : 'text-[var(--text-muted)] hover:text-[var(--text)]',
                            )}
                          >
                            {child.title}
                            {child.target === '_blank' && (
                              <ExternalLinkIcon
                                className="h-3 w-3 shrink-0 opacity-60"
                                aria-hidden="true"
                              />
                            )}
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            );
          }

          return (
            <NavigationMenuItem
              key={key}
              ref={(el: HTMLLIElement | null) => setItemRef(key, el)}
              className={item.placement === 'end' ? 'ml-auto' : undefined}
            >
              <NavigationMenuLink asChild>
                <Link
                  to={item.path}
                  target={item.target}
                  data-active={active ? '' : undefined}
                  className={cn(
                    'relative inline-flex items-center whitespace-nowrap py-3.5 font-mono text-[12px] uppercase tracking-[0.1em] no-underline transition-colors',
                    active
                      ? 'font-medium text-[var(--text)]'
                      : 'text-[var(--text-muted)] hover:text-[var(--text)]',
                  )}
                >
                  {item.title}
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>
          );
        })}

        <li aria-hidden="true" className="pointer-events-none contents">
          <span
            className="pointer-events-none absolute -bottom-px left-0 h-0.5 bg-[var(--accent)] transition-[transform,width,opacity] duration-300 ease-out"
            style={{
              width: box?.width ?? 0,
              opacity: box ? 1 : 0,
              transform: `translateX(${box?.left ?? 0}px)`,
            }}
          />
        </li>
      </NavigationMenuList>
    </NavigationMenu>
  );
};

export default Nav;
